import React from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import {useNotifications} from "context/NotificationContext";
import {invalidate} from "services/query-client";
import Page from "components/Page";
import {MembershipPlanData} from "types/MembershipPlan";
import MembershipPlanForm from "pages/MembershipPlan/Form";
import {FormikHelpers} from "formik";
import {useOperator} from "../../../context/OperatorContext";

function MutateMembershipPlan() {
    const {termFor} = useOperator();
    const {id} = useParams()
    const {data: membershipPlan, isLoading} = useQuery(
        ['membership-plans', {id}],
        () => ngage.membershipPlans.get(parseInt(id as string)),
        {enabled: !!id}
    )

    const store = useMutation((data: MembershipPlanData) => ngage.membershipPlans.store(data))
    const update = useMutation((data: MembershipPlanData) => ngage.membershipPlans.update(parseInt(id as string), data))

    const {notify} = useNotifications()
    const navigate = useNavigate()

    // Handle form submission
    async function handleSubmit(values: MembershipPlanData, {setSubmitting}: FormikHelpers<MembershipPlanData>) {
        id? await handleUpdate(values) : await handleStore(values)
        await invalidate('membership-plans.all')

        setSubmitting(false)
        navigate('/membership-plans')
    }

    // Handle store mutation
    async function handleStore(values: MembershipPlanData) {
        try {
            await store.mutateAsync(values)
            notify("success", `${termFor('Membership Plan')} Created`, `${values.name} has been created`)
        } catch (error) {
            notify("error", `${termFor('Membership Plan')} Not Created`, `${values.name} could not be stored`)
            throw error
        }
    }

    // Handle update mutation
    async function handleUpdate(values: MembershipPlanData) {
        try {
            await update.mutateAsync(values)
            notify("success", `${termFor('Membership Plan')} Updated`, `${values.name} has been updated`)
            await invalidate(['membership-plans', {id}])
        } catch (error) {
            notify("error", `${termFor('Membership Plan')} Not Updated`, `${values.name} could not be updated`)
            throw error
        }
    }

    // Prepare breadcrumbs
    const newBreadcrumbs = {'Commercial': '/', [termFor('Membership Plans')]: '/membership-plans', [`New ${termFor('Membership Plan')}`]: '/membership-plans/new'}
    const editBreadcrumbs = {'Commercial': '/', [termFor('Membership Plans')]: '/membership-plans', [`Edit ${termFor('Membership Plan')}`]: `/membership-plans/${id}/edit`}

    return (
        <Page title={id ? `Edit ${termFor('Membership Plan')}` : `New ${termFor('Membership Plan')}`}
              breadcrumbs={id ? editBreadcrumbs : newBreadcrumbs}
              isLoading={isLoading}>
            <MembershipPlanForm membershipPlan={membershipPlan} onSubmit={handleSubmit}/>
        </Page>
    );
}

export default MutateMembershipPlan;